import { Collapse, IconButton, Paper, Typography } from "@mui/material"
import { AddOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import { useParams, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useState } from "react"

import { useGetProductWarehouses } from "../hooks/useGetProductWarehouses"

import { ProductWarehouseFormDialog } from "./ProductWarehouseFormDialog"
import { ProductWarehouseTable } from "./ProductWarehouseTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import ToggleInactiveButton from "@/components/common/ToggleInactiveButton"
import type { ProductWarehouseArea } from "@/graphql/codegen/graphql"

interface ProductWarehousesProps {
  isPastProductVersion: boolean
}

export const ProductWarehouses: FC<ProductWarehousesProps> = ({ isPastProductVersion }) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const [editingWarehouseArea, setEditingWarehouseArea] = useState<ProductWarehouseArea | null>(null)
  const [isShowingInactive, setIsShowingInactive] = useState(false)

  const params = useParams({ from: "/app/product/$productId/edit" })
  const { version } = useSearch({ from: "/app/product/$productId/edit/_layout/information" })
  const productId = Number(params.productId)

  const { warehouseAreas, packageUsUomName, refetch } = useGetProductWarehouses({
    productId: Number(productId),
    version,
    includeInactive: isShowingInactive,
  })

  const handleFormDialogOpen = (warehouseArea?: ProductWarehouseArea) => {
    setEditingWarehouseArea(warehouseArea ?? null)
    setIsFormDialogOpen(true)
  }

  const handleFormDialogClose = () => {
    setIsFormDialogOpen(false)
    setEditingWarehouseArea(null)
  }

  const handleFormDialogSubmit = () => {
    refetch({ productId, version, includeInactive: isShowingInactive })
    handleFormDialogClose()
  }

  return (
    <Paper className='border border-gray-400 p-6' elevation={0} data-testid='product.warehouses'>
      <div className='flex items-center gap-x-4'>
        <Typography variant='h6' className='mr-auto'>
          Warehouse Details
        </Typography>
        <ToggleInactiveButton
          showInactive={isShowingInactive}
          onToggle={() => setIsShowingInactive((prev) => !prev)}
          label='Warehouses'
          data-testid='product.warehouses.toggle-inactive-button'
        />
        {!isPastProductVersion && warehouseAreas.length > 0 && (
          <Button
            appearance='outlined'
            startIcon={<AddOutlined />}
            onClick={() => handleFormDialogOpen()}
            data-testid='product.warehouses.add-another-button'
          >
            Add Warehouse Details
          </Button>
        )}
        <IconButton onClick={() => setIsExpanded(!isExpanded)} aria-expanded={isExpanded} aria-label='show more'>
          {isExpanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </div>
      <Collapse in={isExpanded}>
        <div className='mt-6'>
          {warehouseAreas.length > 0 ? (
            <ProductWarehouseTable
              warehouseAreas={warehouseAreas}
              isPastProductVersion={isPastProductVersion}
              packageUsUomName={packageUsUomName}
              onEdit={handleFormDialogOpen}
            />
          ) : (
            <InformationMissingAlert
              title='Add Warehouse Details'
              {...(!isPastProductVersion && {
                action: (
                  <Button
                    color='inherit'
                    size='small'
                    appearance='outlined'
                    className='text-primary'
                    startIcon={<AddOutlined />}
                    onClick={() => handleFormDialogOpen()}
                    data-testid='product.warehouses.add-button'
                  >
                    Add Warehouse Details
                  </Button>
                ),
              })}
            />
          )}
        </div>
      </Collapse>

      {isFormDialogOpen && (
        <ProductWarehouseFormDialog
          warehouseArea={editingWarehouseArea}
          productId={productId}
          version={version}
          onClose={handleFormDialogClose}
          onSubmit={handleFormDialogSubmit}
        />
      )}
    </Paper>
  )
}
